import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, API, getToken } from "../../lib/api";
import { Users, Plus, Save, Trash2, X, Upload, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

function absUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (url.startsWith("/")) return `${base}${url}`;
  return url;
}

async function uploadPhoto(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API}/admin/media`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  });
  if (!res.ok) {
    let msg = "Upload failed";
    try { const j = await res.json(); msg = j.detail || msg; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

const EMPTY = { name: "", job_title: "", photo_url: "", bio: "", sort_order: 100 };

export default function AdminTeam() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/team").then((r) => setMembers(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({
      name: m.name || "",
      job_title: m.job_title || "",
      photo_url: m.photo_url || "",
      bio: m.bio || "",
      sort_order: m.sort_order ?? 100,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    try {
      const { url } = await uploadPhoto(file);
      set("photo_url", url);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setPhotoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.job_title.trim()) {
      toast.error("Name and job title are required.");
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/team/${editingId}`, form);
        toast.success("Team member updated.");
      } else {
        await api.post(`/admin/team`, form);
        toast.success("Team member added.");
      }
      cancelEdit();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    try {
      await api.delete(`/admin/team/${m.id}`);
      toast.success("Removed.");
      if (editingId === m.id) cancelEdit();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not remove.");
    }
  };

  const move = async (m, dir) => {
    // Increase / decrease sort_order by 10 to slot between others
    const next = (m.sort_order ?? 100) + (dir === "up" ? -15 : 15);
    try {
      await api.put(`/admin/team/${m.id}`, { sort_order: next });
      load();
    } catch (err) {
      toast.error("Could not reorder.");
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-team-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-7">
        <div>
          <p className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase">About NBR section</p>
          <h1 className="font-display text-3xl md:text-4xl text-[#04273d] font-semibold tracking-tight mt-1">Team manager</h1>
          <p className="text-sm text-[#447694] mt-1.5">Add team members and photos. These appear on the public About page.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-2 bnp-card p-6 space-y-4 self-start" data-testid="admin-team-form">
          <div className="flex items-center justify-between">
            <p className="font-display font-semibold text-[#04273d]">
              {editingId ? "Edit team member" : "Add a team member"}
            </p>
            {editingId && (
              <button type="button" onClick={cancelEdit} className="text-xs text-[#447694] hover:text-[#04273d] underline" data-testid="admin-team-cancel-edit">
                Cancel edit
              </button>
            )}
          </div>

          <div>
            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Name *</label>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="bnp-input" data-testid="admin-team-name" />
          </div>
          <div>
            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Job title *</label>
            <input required value={form.job_title} onChange={(e) => set("job_title", e.target.value)} className="bnp-input" placeholder="e.g. Director of Operations" data-testid="admin-team-title" />
          </div>

          <div>
            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Photo</label>
            {form.photo_url ? (
              <div className="relative rounded-2xl overflow-hidden border border-[#9ac7f7]/30">
                <img src={absUrl(form.photo_url)} alt="" className="w-full h-44 object-cover" />
                <button type="button" onClick={() => set("photo_url", "")} className="absolute top-2 right-2 bg-white/90 hover:bg-white text-[#04273d] rounded-full p-1.5 shadow" aria-label="Remove photo" data-testid="admin-team-remove-photo">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={photoBusy} className="w-full bg-[#f7f7f7] border-2 border-dashed border-[#9ac7f7]/50 hover:border-[#71edc9] rounded-2xl p-6 flex flex-col items-center gap-2 transition-colors" data-testid="admin-team-upload-photo">
                {photoBusy ? <Loader2 size={20} className="animate-spin text-[#71edc9]" /> : <Upload size={20} className="text-[#447694]" />}
                <span className="font-display font-semibold text-sm text-[#04273d]">{photoBusy ? "Uploading…" : "Upload photo"}</span>
                <span className="text-[11px] text-[#447694]">Square JPG/PNG works best</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onPhotoFile} className="hidden" />
          </div>

          <div>
            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Short bio (optional)</label>
            <textarea rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} className="bnp-input" placeholder="One short sentence about them." data-testid="admin-team-bio" />
          </div>
          <div>
            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Display order (lower = earlier)</label>
            <input type="number" value={form.sort_order} onChange={(e) => set("sort_order", parseInt(e.target.value || "100", 10))} className="bnp-input" data-testid="admin-team-order" />
          </div>

          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="admin-team-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : (editingId ? <Save size={14} /> : <Plus size={14} />)}
            {editingId ? "Save changes" : "Add team member"}
          </button>
        </form>

        {/* List */}
        <div className="lg:col-span-3">
          {loading ? (
            <div className="bnp-card p-8 text-center text-[#447694]">Loading…</div>
          ) : members.length === 0 ? (
            <div className="bnp-card p-10 text-center">
              <Users size={28} className="text-[#9ac7f7] mx-auto" />
              <p className="font-display text-[#04273d] mt-3 font-semibold">No team members yet</p>
              <p className="text-sm text-[#447694] mt-1">Add one with the form on the left to get started.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="admin-team-list">
              {members.map((m, i) => (
                <li key={m.id} className="bnp-card p-4 flex items-center gap-4" data-testid={`admin-team-row-${m.id}`}>
                  <div className="h-16 w-16 rounded-xl bg-[#f7f7f7] flex items-center justify-center overflow-hidden shrink-0">
                    {m.photo_url ? (
                      <img src={absUrl(m.photo_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Users size={18} className="text-[#9ac7f7]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-semibold text-[#04273d] text-base truncate">{m.name}</p>
                    <p className="text-sm text-[#447694] truncate">{m.job_title}</p>
                    {m.bio && <p className="text-xs text-[#447694] mt-1 line-clamp-1">{m.bio}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button type="button" onClick={() => move(m, "up")} disabled={i === 0} className="p-2 rounded-full hover:bg-[#9ac7f7]/15 text-[#04273d] disabled:opacity-30" title="Move up" data-testid={`admin-team-up-${m.id}`}>
                      <ArrowUp size={14} />
                    </button>
                    <button type="button" onClick={() => move(m, "down")} disabled={i === members.length - 1} className="p-2 rounded-full hover:bg-[#9ac7f7]/15 text-[#04273d] disabled:opacity-30" title="Move down" data-testid={`admin-team-down-${m.id}`}>
                      <ArrowDown size={14} />
                    </button>
                    <button type="button" onClick={() => startEdit(m)} className="btn-ghost text-xs" data-testid={`admin-team-edit-${m.id}`}>Edit</button>
                    <button type="button" onClick={() => remove(m)} className="p-2 rounded-full hover:bg-red-50 text-red-600" title="Delete" data-testid={`admin-team-delete-${m.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
